import React, { useEffect, useState } from 'react';
import PageLayout from 'components/layouts/PageLayout';
import ServiceHelper from 'stores/ServiceHelper';
import styled from 'styled-components';
import { useParams } from 'react-router-dom';
import { AmenityModel } from 'types/CommonTypes';
import { useStoreWrapper } from 'stores/StoreHelper';
import { useStateContext } from 'providers/usePageState';
import AmenityForm from 'components/pages/amenity/AmenityForm';
import { InnerGroup, InnerSection } from 'components/layouts/CommonForm';
import Text from 'components/commons/Text';
import { Button, Row } from 'saladsoft-primitive';
import { toast } from 'react-toastify';
import TagItem from 'components/pages/listings/TagItem';
import AmenityItem from 'components/pages/listings/AmenityItem';

const AmenitiesPage = () => {
  const { listingId } = useParams();
  const { uiStore } = useStoreWrapper();
  const { broadcast } = useStateContext();

  const [amenities, setAmenities] = useState<AmenityModel[]>([]);
  const [listingAmenities, setListingAmenities] = useState<AmenityModel[]>([]);
  const [mergedAmenities, setMergedAmenities] = useState<AmenityModel[]>([]);

  const addAllAmenities = () => {
    const isAllSelected = mergedAmenities.every((amenity) => amenity.isChecked);
    const targetAmenities = isAllSelected ? [] : (amenities.map(amenity => amenity.id) as number[]);

    ServiceHelper.adminListingService?.saveAmenities(
      Number(listingId),
      targetAmenities,
    ).then((res) => {
      if (res.status === 204) {
        toast.success('편의시설이 적용되었습니다.');
        fetchAmenities();
      }
    });
  };

  const handleOnChange = async (amenity: AmenityModel) => {
    const updatedAmenities = mergedAmenities.map((item) => {
      if (item.id === amenity.id) {
        return { ...item, isChecked: !item.isChecked };
      }
      return item;
    });

    setMergedAmenities(updatedAmenities);

    await ServiceHelper.adminListingService?.saveAmenities(
      Number(listingId),
      updatedAmenities.filter((item) => item.isChecked).map((item) => item.id) as number[],
    );

    broadcast({ id: 'reload', param: 'amenities' });
  };

  const handleAddAmenity = () => {
    uiStore?.modal.show({
      title: '편의시설 추가',
      style: { width: 800, maxHeight: '80%' },
      children: (
        <div style={{ padding: 20 }}>
          <AmenityForm
            onSave={(amenity: AmenityModel) => {
              fetchSystemAmenities();
            }}
          />
        </div>
      ),
    });
  };

  const fetchAmenities = async () => {
    if (!amenities.length) return;

    ServiceHelper.adminListingService?.getAmenitiesByListing1(Number(listingId), { size: 1000 }).then((res) => {
      const updatedAmenities = amenities.map((apiAmenity) => ({
        ...apiAmenity,
        isChecked: res?.data?.some((item) => item.id === apiAmenity.id) ?? false,
      }));
      setListingAmenities(res.data as AmenityModel[]);
      const newAmenities = JSON.parse(JSON.stringify(updatedAmenities));
      setMergedAmenities(newAmenities);
    });
  };

  const fetchSystemAmenities = async () => {
    ServiceHelper.adminAmenityService?.getAmenities({ size: 1000 }).then((res) => {
      setAmenities(res.data as AmenityModel[]);
    });
  };

  useEffect(() => {
    fetchSystemAmenities();
  }, []);

  useEffect(() => {
    fetchAmenities();
  }, [amenities]);

  return (
    <PageLayout title="편의시설">
      <InnerSection>
        <InnerGroup style={{ minHeight: 'unset' }}>
          <div>
            <Text type="H3" style={{ marginBottom: 10 }}>
              편의시설 지정하기
            </Text>
            <Text type="H4" buttonRole="DESCRIPTION" style={{ marginBottom: 10 }}>
              우측의 편의시설 목록에서 등록하실 편의시설을 선택 해주세요.
              <br />
              선택 즉시 반영됩니다.
            </Text>
            <Button type="outlined" style={{ width: 150 }} onClick={() => handleAddAmenity()}>
              추가하기
            </Button>
          </div>
          <div style={{ width: '100%', flex: 1 }}>
            <Row marginBottom="20px" justifyContent="flex-start" gap="10px">
              <Button type="outlined" style={{ width: 150 }} onClick={() => addAllAmenities()}>
                {mergedAmenities.filter((amenity) => amenity.isChecked).length === amenities.length
                  ? '전체 선택해제'
                  : '전체추가'}
              </Button>
            </Row>
            <Row flexWrap="wrap" width="100%" justifyContent="flex-start">
              {mergedAmenities?.map((amenity) => (
                <AmenityItem
                  key={amenity.id}
                  defaultChecked={amenity.isChecked}
                  amenity={amenity}
                  onChange={(amenity) => handleOnChange(amenity)}
                />
              ))}
            </Row>
          </div>
        </InnerGroup>
      </InnerSection>
    </PageLayout>
  );
};

const Wrapper = styled.div`
  display: inline-flex;
  align-items: center;
  padding: 10px 20px;
  border: 1px solid #efefef;
  gap: 10px;
  margin: 10px;
  border-radius: 10px;
`;

export default AmenitiesPage;
